/**
 * React Query hooks for the generic workflow engine.
 *
 * Public surface for any feature that owns a `workflow_instance_uuid` -
 * Holding pages, Claim pages, and the cross-resource "Pending approvals"
 * inbox all import from here.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  approvePending,
  createExceptionActionBinding,
  createExceptionGrant,
  createExceptionReason,
  deleteExceptionActionBinding,
  deleteExceptionReason,
  denyPending,
  dismissWorkflowIssue,
  executeWorkflowAction,
  getVisibleActions,
  getVisibleActionsBatch,
  getWorkflowHistory,
  getWorkflowInstance,
  getWorkflowIssue,
  listEntityLog,
  listExceptionActionBindings,
  listExceptionActionCatalog,
  listExceptionReasons,
  listInstanceWorkflowIssues,
  listPendingApprovals,
  listWorkflowIssues,
  listWorkflowIssuesPage,
  lookupWorkflowInstance,
  resolveWorkflowIssue,
  updateExceptionActionBinding,
  updateExceptionReason,
} from '@/shared/lib/workflows/api'
import type {
  ExceptionGrantCreatePayload,
  ExecuteActionPayload,
  WorkflowIssueDismissPayload,
  WorkflowIssueListParams,
  WorkflowIssueResolvePayload,
  WorkflowIssueStatus,
} from '@/shared/lib/workflows/types'

const KEY = ['workflows'] as const

export function useWorkflowInstance(uuid: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'instance', uuid],
    queryFn: () => getWorkflowInstance(uuid as string),
    enabled: Boolean(uuid),
  })
}

/**
 * Look up the workflow instance for a domain resource by
 * (resource_type, resource_id). Lets pages mount the workflow UI
 * without their own serializers having to surface the FK.
 */
export function useWorkflowInstanceLookup(
  resourceType: string | null | undefined,
  resourceId: string | null | undefined,
) {
  return useQuery({
    queryKey: [...KEY, 'lookup', resourceType, resourceId],
    queryFn: () =>
      lookupWorkflowInstance(resourceType as string, resourceId as string),
    enabled: Boolean(resourceType && resourceId),
  })
}

export function useWorkflowVisibleActions(uuid: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'visible-actions', uuid],
    queryFn: () => getVisibleActions(uuid as string),
    enabled: Boolean(uuid),
  })
}

/**
 * Batch counterpart of {@link useWorkflowVisibleActions}. Fetches
 * visibility for all supplied instance uuids in a single request, so
 * pages with many workflow-action rows do not issue one HTTP request
 * per row.
 *
 * The returned data is a map keyed by instance uuid - falsy/duplicate
 * uuids are filtered out before the request fires. The query is
 * disabled until at least one uuid is present.
 */
export function useWorkflowVisibleActionsBatch(
  uuids: ReadonlyArray<string | null | undefined>,
) {
  const cleaned = Array.from(
    new Set(uuids.filter((u): u is string => Boolean(u))),
  ).sort()
  return useQuery({
    queryKey: [...KEY, 'visible-actions-batch', cleaned],
    queryFn: () => getVisibleActionsBatch(cleaned),
    enabled: cleaned.length > 0,
  })
}

export function useWorkflowHistory(uuid: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'history', uuid],
    queryFn: () => getWorkflowHistory(uuid as string),
    enabled: Boolean(uuid),
  })
}

export function usePendingApprovals() {
  return useQuery({
    queryKey: [...KEY, 'pending'],
    queryFn: listPendingApprovals,
  })
}

function invalidateInstance(qc: ReturnType<typeof useQueryClient>, uuid: string) {
  qc.invalidateQueries({ queryKey: [...KEY, 'instance', uuid] })
  qc.invalidateQueries({ queryKey: [...KEY, 'visible-actions', uuid] })
  qc.invalidateQueries({ queryKey: [...KEY, 'history', uuid] })
  qc.invalidateQueries({ queryKey: [...KEY, 'pending'] })
}

/**
 * Invalidate every query backing a single workflow instance (the progress
 * rail, status badge, and visible-actions menu). Exported so bespoke domain
 * mutations that drive a transition outside {@link useExecuteWorkflowAction}
 * - e.g. an inspection's "confirm appointment" endpoint, which advances the
 * engine as a side-effect - can keep those workflow surfaces in lockstep
 * with the resource they just changed. A no-op when `uuid` is falsy.
 */
export function invalidateWorkflowInstanceQueries(
  qc: ReturnType<typeof useQueryClient>,
  uuid: string | null | undefined,
) {
  if (!uuid) return
  invalidateInstance(qc, uuid)
}

/**
 * Refetch every open visible-actions menu. What an action offers can follow
 * the record's data (a capture field's ``default_from``), so a domain save
 * that changes that data refreshes the menus without knowing the instance.
 */
export function invalidateVisibleActions(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [...KEY, 'visible-actions'] })
}

export function useExecuteWorkflowAction(instanceUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: ExecuteActionPayload) =>
      executeWorkflowAction(instanceUuid, payload),
    onSuccess: () => invalidateInstance(qc, instanceUuid),
  })
}

export function useApprovePending() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      note,
      correlation_id,
    }: {
      uuid: string
      note?: string
      correlation_id?: string
    }) => approvePending(uuid, { note, correlation_id }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: [...KEY, 'pending'] })
      if (data?.instance?.uuid) {
        invalidateInstance(qc, data.instance.uuid)
      }
    },
  })
}

export function useDenyPending() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, reason }: { uuid: string; reason: string }) =>
      denyPending(uuid, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY, 'pending'] })
    },
  })
}

// ---------------------------------------------------------------------------
// WorkflowIssue
// ---------------------------------------------------------------------------

const ISSUE_KEY = [...KEY, 'issues'] as const
const EXCEPTIONS_KEY = [...KEY, 'exceptions'] as const
export const ENTITY_LOG_KEY = [...KEY, 'entity-log'] as const

function invalidateIssueRoots(qc: ReturnType<typeof useQueryClient>) {
  // Invalidate every variant of the issues list/detail queries plus
  // the holding/transfer caches so banners refresh automatically.
  qc.invalidateQueries({ queryKey: ISSUE_KEY })
  qc.invalidateQueries({ queryKey: ['holdings'] })
  qc.invalidateQueries({ queryKey: ['holding'] })
  // Property/structure aggregate views: refresh the per-structure
  // "needs action" counts so denying an exception (which can revert the
  // warranty class and clear the blocker) updates the property view too.
  qc.invalidateQueries({ queryKey: ['host-structures'] })
  qc.invalidateQueries({ queryKey: ['transfers'] })
}

/**
 * One page of the Exceptions list (server-side search, filters and pages).
 * Keeps the last page while the next loads, so the Filters panel's facets
 * (which ride on the page) stay put.
 */
export function useWorkflowIssuesPage(params: WorkflowIssueListParams) {
  return useQuery({
    queryKey: [...ISSUE_KEY, 'page', params],
    queryFn: () => listWorkflowIssuesPage(params),
    placeholderData: keepPreviousData,
  })
}

/**
 * List workflow issues for the current tenant. Re-runs whenever the
 * filter object identity changes; pass a stable object (memo or
 * object literal in deps) to avoid stale closures.
 */
export function useWorkflowIssues(params: WorkflowIssueListParams = {}) {
  return useQuery({
    queryKey: [...ISSUE_KEY, 'list', params],
    queryFn: () => listWorkflowIssues(params),
  })
}

export function useWorkflowIssue(uuid: string | null | undefined) {
  return useQuery({
    queryKey: [...ISSUE_KEY, 'detail', uuid],
    queryFn: () => getWorkflowIssue(uuid as string),
    enabled: Boolean(uuid),
  })
}

/**
 * Issues attached to a single WorkflowInstance - convenience wrapper
 * for resource detail pages that already have the instance UUID.
 * Defaults to OPEN-only so the typical "blocking banner" case
 * doesn't accidentally surface resolved history.
 */
export function useWorkflowInstanceIssues(
  instanceUuid: string | null | undefined,
  statuses?: WorkflowIssueStatus[],
) {
  return useQuery({
    queryKey: [...ISSUE_KEY, 'instance', instanceUuid, statuses ?? null],
    queryFn: () =>
      listInstanceWorkflowIssues(instanceUuid as string, statuses),
    enabled: Boolean(instanceUuid),
  })
}

export function useResolveWorkflowIssue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string
      payload?: WorkflowIssueResolvePayload
    }) => resolveWorkflowIssue(uuid, payload ?? {}),
    onSuccess: () => invalidateIssueRoots(qc),
  })
}

export function useDismissWorkflowIssue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string
      payload?: WorkflowIssueDismissPayload
    }) => dismissWorkflowIssue(uuid, payload ?? {}),
    onSuccess: () => invalidateIssueRoots(qc),
  })
}

export function useExceptionReasons(enabled = true) {
  return useQuery({
    queryKey: [...EXCEPTIONS_KEY, 'reasons'],
    queryFn: listExceptionReasons,
    enabled,
  })
}

export function useCreateExceptionReason() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createExceptionReason,
    onSuccess: () => qc.invalidateQueries({ queryKey: EXCEPTIONS_KEY }),
  })
}

export function useUpdateExceptionReason() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string
      payload: Parameters<typeof updateExceptionReason>[1]
    }) => updateExceptionReason(uuid, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: EXCEPTIONS_KEY }),
  })
}

export function useDeleteExceptionReason() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteExceptionReason,
    onSuccess: () => qc.invalidateQueries({ queryKey: EXCEPTIONS_KEY }),
  })
}

export function useExceptionActionBindings(enabled = true) {
  return useQuery({
    queryKey: [...EXCEPTIONS_KEY, 'bindings'],
    queryFn: listExceptionActionBindings,
    enabled,
  })
}

export function useExceptionActionCatalog() {
  return useQuery({
    queryKey: [...EXCEPTIONS_KEY, 'action-catalog'],
    queryFn: listExceptionActionCatalog,
  })
}

export function useCreateExceptionActionBinding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createExceptionActionBinding,
    onSuccess: () => qc.invalidateQueries({ queryKey: EXCEPTIONS_KEY }),
  })
}

export function useUpdateExceptionActionBinding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string
      payload: Parameters<typeof updateExceptionActionBinding>[1]
    }) => updateExceptionActionBinding(uuid, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: EXCEPTIONS_KEY }),
  })
}

export function useDeleteExceptionActionBinding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteExceptionActionBinding,
    onSuccess: () => qc.invalidateQueries({ queryKey: EXCEPTIONS_KEY }),
  })
}

export function useCreateExceptionGrant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: ExceptionGrantCreatePayload) => createExceptionGrant(payload),
    onSuccess: (grant) => {
      qc.invalidateQueries({ queryKey: EXCEPTIONS_KEY })
      qc.invalidateQueries({ queryKey: ENTITY_LOG_KEY })
      if (grant.entity_id) {
        qc.invalidateQueries({ queryKey: ['holding', grant.entity_id] })
      }
    },
  })
}

export function useEntityLog(params: {
  entity_key?: string
  entity_id?: string
  workflow_instance?: string
  page_size?: number
}) {
  return useQuery({
    queryKey: [...ENTITY_LOG_KEY, params],
    queryFn: () => listEntityLog(params),
    enabled: Boolean(params.entity_id || params.workflow_instance),
  })
}
